import React, { Fragment, useState, useEffect } from 'react';
import { PAGES, ACTIONS, usePermissions } from 'hooks/permissions/permissions';
import StatusBadge from 'components/statusBadge';
import { Row, Col, Button, Input, Pagination, message, Popconfirm } from 'antd';
import { QuestionCircleOutlined, SortDescendingOutlined, SortAscendingOutlined } from '@ant-design/icons';
import { useDepartmentList, useDeleteDepartment, useStatusDepartment } from "hooks/configuration/department/Department";
import { Link } from 'react-router-dom';
import { statusColor, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE } from "helpers/commonFunctions";
import Image from 'helpers/Image';
import "../style.css";
import TableStateRow from "components/tableState";

const Department = () => {

    const { can } = usePermissions();
    const canAdd = can(PAGES.CONTRACTOR, ACTIONS.ADD);
    const canEdit = can(PAGES.CONTRACTOR, ACTIONS.EDIT);
    const canDelete = can(PAGES.CONTRACTOR, ACTIONS.DELETE);
    const showActions = canEdit || canDelete;
    const columnCount = showActions ? 3 : 2;

    const [page, setPage] = useState(DEFAULT_PAGE_SIZE);
    const [listData, setListData] = useState(null);
    const [pageNumber, setPageNumber] = useState(0);
    const [defaultCall, setDefaultCall] = useState(false);
    const [searchKey, setSearchKey] = useState("");
    const [sortField, setSortField] = useState("");
    const [sortMethod, setSortMethod] = useState("desc");
    const [initValues, setInitValues] = useState({
        "pageNumber": pageNumber,
        "pageSize": page,
        "searchKey": searchKey,
        "sortField": sortField,
        "sortMethod": sortMethod
    });

    const [sorting, setSorting] = useState({
        "d.department_name": null,
        "d.active": null
    });

    const { mutate: departmentList, data: departmentListData, isLoading: Loading } = useDepartmentList();
    const { mutate: deleteDepartment, isLoading: deleteLoading } = useDeleteDepartment();
    const { mutate: statusChange } = useStatusDepartment();
    const onSortChange = async (column) => {
        initValues.pageNumber = 0;
        initValues.sortField = column;
        initValues.sortMethod = sorting[column] === 'asc' ? 'desc' : 'asc';
        setPageNumber(0);
        const newSorting = {
            ...sorting,
            [column]: sorting[column] === 'asc' ? 'desc' : 'asc',
        };
        setSorting(newSorting);
        setInitValues(initValues);
        setSortField(column);
        setSortMethod(newSorting[column]);
        setListData([]);
    };
    const deleteId = (id) => {
        deleteDepartment(id, {
            onSuccess: (item) => {
                if (item.statusCode === 200) {
                    message.success(item.message);
                    onFetchDepartmentList(initValues);
                }
                else {
                    message.error(item?.message);
                }
            },
        });
    }


    const handleConfirmActive = (id) => {
        statusChange(id, {
            onSuccess: (item) => {
                if (item.statusCode === 200) {
                    message.success(item.message);
                    onFetchDepartmentList(initValues);
                }
                else {
                    message.error(item?.message);
                }
            },
        });
    }

    const onFetchDepartmentList = (values) => {
        departmentList(values, {
            onSuccess: (item) => {
                setListData(item?.data?.content);
            }
        })
    }

    const onChange = (page) => {
        initValues.pageNumber = page - 1;
        setInitValues(initValues);
        setPageNumber(page - 1);
    }



    const onShowSizeChange = (current, pageSize) => {
        setPage(initValues.pageSize = pageSize)
    }

    const onSearchHandler = (e) => {
        initValues.searchKey = e.target.value;
        let data = { ...initValues };
        data.searchKey = e.target.value;
        data.pageNumber = e.target.value !== "" ? 0 : initValues.pageNumber;
        e.target.value !== "" ? data.pageNumber = 0 : data.pageNumber = pageNumber;
        setInitValues(data);
        setSearchKey(e.target.value);
    }

    useEffect(() => {
        setDefaultCall(true);
    }, [])

    useEffect(() => {
        if (defaultCall) {
            onFetchDepartmentList(initValues);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultCall, pageNumber, searchKey, page, sortField, sortMethod]);

    return (
        <Fragment>
            <div className="common-head">
                <div className="title">
                    <h1>Contractors</h1>
                    <p>The firms supplying labour to the site</p>
                </div>
                <div className="txt-right single-line">
                    <Input placeholder='Search' prefix={<Image className="search-img" name={"search.svg"} />} allowClear onChange={onSearchHandler} />
                    {!canAdd ? null : <Link to="/configuration/department/create">
                        <Button><Image name="plus.png" /> New Contractor</Button>
                    </Link>}
                </div>
            </div>
            <div className="common-table">
                <table>
                    <thead>
                        <tr>
                            <th>Contractor Name {sorting["d.department_name"] === 'asc' ? <SortAscendingOutlined onClick={() => onSortChange("d.department_name")} /> : <SortDescendingOutlined onClick={() => onSortChange("d.department_name")} />}</th>
                            <th>Status {sorting["d.active"] === 'asc' ? <SortAscendingOutlined onClick={() => onSortChange("d.active")} /> : <SortDescendingOutlined onClick={() => onSortChange("d.active")} />}</th>
                            {showActions ? <th>Action</th> : null}
                        </tr>
                    </thead>
                    <tbody>
                        {Loading ? (
                            <TableStateRow colSpan={columnCount} loading />
                        ) : listData == null || listData === undefined || listData?.length === 0 ? (
                            <TableStateRow colSpan={columnCount}>No contractors found</TableStateRow>
                        ) : listData.map((item, a) => (
                                <tr key={a}>
                                    <td>{item?.departmentName}</td>

                                    <td>
                                        {canEdit ? <Popconfirm title={`Are you sure you want to ${item?.status === true ? "inactivate" : "activate"} this ${item?.departmentName}?`}
                                            onConfirm={() => handleConfirmActive(item.departmentId)}
                                            icon={
                                                <QuestionCircleOutlined />
                                            }
                                            okText="Yes"
                                            cancelText="No">
                                            <button className={statusColor(item.status)}>
                                                {item?.status ? "Active" : "InActive"}
                                            </button>
                                        </Popconfirm> : <StatusBadge active={item?.status} />}
                                    </td>

                                    {showActions ? <td>
                                        {!canEdit ? null : <Link to={`/configuration/department/${item.departmentId}`} state={item}>
                                            <Image name="edit.png" />
                                        </Link>}
                                        {!canDelete ? null : <Popconfirm
                                            Loading={deleteLoading}
                                            title={`Are you sure to delete ${item?.departmentName}?`}
                                            onConfirm={() => deleteId(item?.departmentId)}
                                            icon={<QuestionCircleOutlined />}
                                            okText="Yes"
                                            cancelText="No"
                                        >
                                            <Image name="delete.png" />
                                        </Popconfirm>}
                                    </td> : null}
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            <Row className="table-pagination">
                <Col md={12}>
                    <h5>Showing {pageNumber === 0 ? 1 : (departmentListData?.data?.pageable?.offset + 1 || 0)} to {page === 0 ? (departmentListData?.data?.pageable?.offset + listData?.length || 0) : (departmentListData?.data?.pageable?.offset + departmentListData?.data?.numberOfElements || 0)} of {departmentListData?.data?.totalElements} entries</h5>
                </Col>
                <Col md={12} className="txt-right">
                    {departmentListData?.data?.content?.length === 0 ? null : <Pagination pageSize={page || DEFAULT_PAGE_SIZE}showSizeChanger pageSizeOptions={PAGE_SIZE_OPTIONS} onChange={onChange} current={pageNumber + 1} onShowSizeChange={onShowSizeChange} total={departmentListData?.data?.totalElements} />}
                </Col>
            </Row>
        </Fragment >
    )
}

export default Department;
